import { Box, Divider } from "@mui/joy";
import { useState, useRef } from "react";
import { TaskActionComponent, TaskHeader } from "../Tasks/components";
import {
    RoofMeasurements,
    LineItems,
    UnsavedChangesBanner,
} from "./components";
import { EstimateContractContext } from "./context";
import { useInit, useSync, useMaterialsInit, useLaborInit } from "./hooks";
import useEstimateContractContextManager from "./hooks/useEstimateContractContextManager";

const emptyValues = () => ({
    materials: {},
    labor: {},
    measurements: {},
    attributes: {},
    attributeIds: {},
    cartIds: {},
});

export default function EstimateContract({ activeTask, onComplete }: any) {
    const [values, setValues] = useState(emptyValues);
    const referenceValue = useRef(emptyValues()).current;
    const [initLoading, setInitLoading] = useState(false);

    return (
        <EstimateContractContext.Provider
            value={{
                values,
                setValues,
                referenceValue,
                initLoading,
                setInitLoading,
            }}
        >
            <EstimateContractContent
                activeTask={activeTask}
                onComplete={onComplete}
            />
        </EstimateContractContext.Provider>
    );
}

function EstimateContractContent({
    activeTask,
    onComplete,
}: {
    activeTask: any;
    onComplete?: () => void;
}) {
    const { Id, ProjectId } = activeTask || {};
    useInit({ projectId: ProjectId });

    const {
        materialsByGroup,
        loading: materialsLoading,
        error: materialsError,
    } = useMaterialsInit(ProjectId);

    const {
        laborRows,
        loading: laborLoading,
        error: laborError,
    } = useLaborInit(ProjectId);

    const { changeIdentified, flushSave, syncing } = useSync({
        projectId: ProjectId,
        materialsByGroup,
    });
    const { values } = useEstimateContractContextManager();
    const handleSave = () => {
        void flushSave();
    };

    const handleComplete = async () => {
        const result = await flushSave();
        if (!result?.success) {
            return;
        }

        if (onComplete) {
            onComplete();
        }
    };

    return (
        <Box sx={{ position: "relative", minHeight: 400 }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    mt: 1,
                    px: { xs: 2, sm: 3 },
                    animation: "fadeIn 0.5s ease-in-out",
                    "@keyframes fadeIn": {
                        "0%": { opacity: 0 },
                        "100%": { opacity: 1 },
                    },
                }}
            >
                <TaskHeader activeTask={activeTask} />
                <Divider />

                <RoofMeasurements />
                <LineItems
                    projectId={ProjectId}
                    materialsByGroup={materialsByGroup}
                    materialsLoading={materialsLoading}
                    materialsError={materialsError}
                    laborRows={laborRows}
                    laborLoading={laborLoading}
                    laborError={laborError}
                />

                <Box mt={4}>
                    <Divider />
                    <TaskActionComponent
                        taskName={activeTask?.Name || ""}
                        isNextEligible={true}
                        actionProgress={{ loading: syncing }}
                        onComplete={handleComplete}
                        taskId={Id || ""}
                    />
                </Box>
            </Box>

            <UnsavedChangesBanner
                open={changeIdentified}
                syncing={syncing}
                resetKey={values}
                onSave={handleSave}
            />
        </Box>
    );
}
